"use client";

import type { CarouselSection as CarouselSectionType } from "@/sanity.types";
import { RichText } from "@/components/richText/richText.component";
import Image from "next/image";
import { urlFor } from "@/sanity/utils";
import { CarouselCentered } from "@/components/carouselCentered/carouselCentered.component";
import { useRef, useState, useEffect } from "react";

export const CarouselSection = ({ block }: CarouselSectionProps) => {
  const cards = block.cards ?? [];

  if (cards.length === 0) return null;

  function cn(...classes: Array<string | false | null | undefined>): string {
    return classes.filter(Boolean).join(" ");
  }

  return (
    <section
      data-testid="carousel-section"
      className="bg-primary-100 flex w-full flex-col items-center gap-6 px-12 py-10">
      <div className="self-stretch" data-testid="carousel-section-title">
        <RichText>{block?.title}</RichText>
      </div>
      <div>
        {block?.subtitle && (
          <div
            className="mb-8 font-sans text-[14px] font-normal lg:mb-6 lg:pr-14 lg:text-[18px]"
            data-testid="carousel-section-subtitle">
            <RichText>{block.subtitle}</RichText>
          </div>
        )}

        {block?.disclaimer && (
          <div data-testid="carousel-section-disclaimer" className="mt-2">
            <RichText>{block.disclaimer}</RichText>
          </div>
        )}
      </div>

      {/* Carousel */}
      <div className="w-full min-w-0 lg:flex-1">
        <CarouselCentered
          className="w-full min-w-0"
          swiperItemsClassName="self-stretch"
          slideInnerClassName="h-full"
          mobileSlidesPerView={1.05}
          desktopSlidesPerView={4}
          mobileSpaceBetween={16}
          desktopSpaceBetween={24}
          mobileSlidesPerGroup={1}
          desktopSlidesPerGroup={2}
          centeredSlides
          desktopCenteredSlides={false}
          initialSlide={0}
          showNavigation={true}
          showPagedProgress={true}
          equalSlideHeights>
          {cards.map((card) => (
            <article
              key={card._key}
              data-testid="homepage-carousel-card"
              className={cn(
                "flex h-full min-w-0 flex-col justify-start rounded-2xl bg-white p-6 md:p-8",
              )}>
              <div className="flex h-16 items-center justify-center">
                {card.image
                  ? (() => {
                      const imageUrl = card.image.asset
                        ? urlFor(card.image).height(64).url()
                        : undefined;

                      return imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt={(card.image as any)?.alt ?? ""}
                          width={538}
                          height={397}
                          className="h-auto max-h-16 w-auto object-contain"
                        />
                      ) : null;
                    })()
                  : null}
              </div>

              <div className="mt-6">
                {card.quote ? <QuoteWithSeeMore quote={card.quote} /> : null}
              </div>
            </article>
          ))}
        </CarouselCentered>
      </div>

      {block?.footer && (
        <div data-testid="carousel-section-footer" className="mt-2 mb-4">
          <RichText>{block.footer}</RichText>
        </div>
      )}
    </section>
  );
};

const QuoteWithSeeMore = ({
  quote,
}: {
  quote: CarouselSectionCard["quote"];
}) => {
  const measureRef = useRef<HTMLDivElement>(null);

  const [showSeeMore, setShowSeeMore] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const element = measureRef.current;
    if (!element) return;

    const checkLines = () => {
      const styles = window.getComputedStyle(element);
      const lineHeight = parseFloat(styles.lineHeight);

      if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

      const totalLines = Math.ceil(element.scrollHeight / lineHeight);

      setShowSeeMore(totalLines > 5);
    };

    checkLines();

    const observer = new ResizeObserver(checkLines);
    observer.observe(element);

    return () => observer.disconnect();
  }, [quote]);

  return (
    <div className="text-greyscale-headline font-brown-std">
      {/* Hidden content used to measure the full quote */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute">
        <RichText>{quote}</RichText>
      </div>

      {/* Visible quote */}
      <div className={!expanded && showSeeMore ? "line-clamp-4" : ""}>
        <RichText>{quote}</RichText>
      </div>

      {showSeeMore && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-primary-400 mt-1 underline">
          See more
        </button>
      )}
    </div>
  );
};

export type CarouselSectionProps = {
  block: CarouselSectionType;
};

export type CarouselSectionCard = NonNullable<
  CarouselSectionType["cards"]
>[number];
